import type { CaptureResult, Properties } from "posthog-js";

/**
 * The query or fragment of a URL that carries a session's credentials, from its
 * `?` or `#` to the end of the URL. The search never crosses another `?` or `#`,
 * so a query beside a credentialed fragment stays, and a long run of markup
 * full of `#` is read once rather than once per `#`.
 *
 * The sign-in broker returns to the app with the session in the fragment —
 * `#access_token=…&refresh_token=…` — and the SDK reads the address before the
 * auth client clears it. A refresh token mints sessions for whoever reads it.
 */
const CREDENTIALS =
  /[?#][^\s"'<>?#]*?\b(?:access|refresh|provider|provider_refresh)_token=[^\s"'<>]*/g;

const isRecord = (value: unknown): value is Properties =>
  !!value && Object.getPrototypeOf(value) === Object.prototype;

/**
 * Copied only along the path to a change: every replay batch passes through
 * here, and nearly none of them holds a credential.
 */
function scrub(value: unknown): unknown {
  if (typeof value === "string") return value.replace(CREDENTIALS, "");
  if (Array.isArray(value)) {
    const items = value.map(scrub);
    return items.some((item, i) => item !== value[i]) ? items : value;
  }
  return isRecord(value) ? scrubRecord(value) : value;
}

function scrubRecord(record: Properties): Properties {
  let copy: Properties | null = null;
  for (const [key, item] of Object.entries(record)) {
    const scrubbed = scrub(item);
    if (scrubbed === item) continue;
    copy ??= { ...record };
    copy[key] = scrubbed;
  }
  return copy ?? record;
}

/**
 * Every URL an event carries, without credentials — wherever the SDK put it:
 * the page, a web vital's own copy of it, the person's first URL, and the
 * replay's snapshots, which reach `before_send` like any other event.
 */
export function withoutUrlCredentials(
  result: CaptureResult | null,
): CaptureResult | null {
  if (!result) return result;
  const properties = scrubRecord(result.properties);
  const $set = result.$set && scrubRecord(result.$set);
  const $set_once = result.$set_once && scrubRecord(result.$set_once);
  return properties === result.properties &&
    $set === result.$set &&
    $set_once === result.$set_once
    ? result
    : { ...result, properties, $set, $set_once };
}
